import * as React from "react";
import { PureComponent } from "react";
import ReactTooltip from "react-tooltip";
import { GetMappedLogEntries } from "source/app/presentation/Header/Tools/DeepWorkKipsToday/DeepWorkKipsChart/Helpers/GetMappedLogEntries";
import { IMappedMonumentalRecord } from "source/app/presentation/Header/Tools/DeepWorkKipsToday/DeepWorkKipsChart/Helpers/MappedMonumentalRecord";
import { LeadingQuote } from "source/app/presentation/Header/Tools/DeepWorkKipsToday/DeepWorkKipsChart/LeadingQuote/LeadingQuote";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import {
    IEmotionCharacterCount,
    resourceNameEmotionCharacterCounts,
} from "../../../../../../../../motw_core/DTOs/emotioncharactercount";
import {
    ILogEntry,
    resourceNameLogEntry,
} from "../../../../../../../../motw_core/Entity/ILogEntry";
import { Sounds } from "../../../../../services/Sound/Sounds";
import { RenderDiv } from "../../../../Shared/Render";
import { DisplayDWKCRecord } from "./DisplayDWKCRecord/DisplayDWKCRecord";
import {
    LoadMoreDataLink,
    LoadMoreDKWCData,
} from "./LoadMoreDataLink/LoadMoreDataLink";

export interface IDeepWorkKipsRecord {
    significance: number;
    date: string;
    value: number;
    monumentalRecords?: IMappedMonumentalRecord[];
    relevantCharacterCounts: IEmotionCharacterCount[];
    numLogEntries: number;
}

export interface IDeepWorkKipsChartState {
    dwkc_log: ILogEntry[];
    dwkc_emotionCharacterCounts?: IEmotionCharacterCount[];
    oldestDate: string;
    isLoading: boolean;
}

export class DeepWorkKipsChart extends PureComponent<
    {},
    IDeepWorkKipsChartState
> {
    constructor(props: {}) {
        super(props);

        this.state = {
            dwkc_log: [],
            dwkc_emotionCharacterCounts: undefined,
            oldestDate: dwkc_oldestDate,
            isLoading: true,
        };
    }

    async componentDidMount() {
        Sounds.fanfareTrumpets.Play();

        try {
            await LoadDWKCDataAsync(this);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    async componentDidUpdate(
        prevProps: {},
        prevState: IDeepWorkKipsChartState
    ) {
        if (this.state.oldestDate !== prevState.oldestDate) {
            await LoadDWKCDataAsync(this);
        }
    }

    render() {
        return <DisplayDWKC dwkc={this} />;
    }
}

export default DeepWorkKipsChart;

function DisplayDWKC({ dwkc }: { dwkc: DeepWorkKipsChart }) {
    React.useEffect(() => {
        const scrollableElement = document.getElementById("childContainer")!;
        scrollableElement.addEventListener("scroll", HandleScroll);

        return () =>
            scrollableElement.removeEventListener("scroll", HandleScroll);
    }, []);

    function HandleScroll() {
        const scrollableElement = document.getElementById("childContainer")!;

        const isAtScrollBottom =
            scrollableElement.scrollTop + scrollableElement.offsetHeight + 1 >=
            scrollableElement.scrollHeight;

        if (isAtScrollBottom) {
            LoadMoreDKWCData(dwkc);
        }
    }

    const mappedRecords = GetMappedLogEntries(
        dwkc.state.dwkc_log,
        dwkc.state.dwkc_emotionCharacterCounts
    );

    if (
        mappedRecords.length === 0 ||
        dwkc.state.dwkc_emotionCharacterCounts === undefined
    ) {
        return (
            <LeadingQuote
                isLoading={dwkc.state.isLoading}
                recordCount={mappedRecords.length}
            />
        );
    }

    return (
        <span>
            <LeadingQuote
                isLoading={dwkc.state.isLoading}
                recordCount={mappedRecords.length}
            />
            <RenderDiv id="DeepWorkData" when={mappedRecords.length}>
                {mappedRecords.map((row, index) => {
                    // Prevent rows without enough data from showing
                    if (row.date < dwkc.state.oldestDate) {
                        return null;
                    }

                    return (
                        <DisplayDWKCRecord
                            row={row}
                            index={index}
                            key={index}
                        />
                    );
                })}
                <LoadMoreDataLink dwkc={dwkc} />
            </RenderDiv>
        </span>
    );
}

async function LoadDWKCDataAsync(dwkc: DeepWorkKipsChart) {
    var logData = await ClientDataProvider.GetRecordsAsync<ILogEntry>(
        `${resourceNameLogEntry}?lastmodifiedtime=gt.${dwkc.state.oldestDate}`
    );

    var eccData =
        await ClientDataProvider.GetRecordsAsync<IEmotionCharacterCount>(
            `${resourceNameEmotionCharacterCounts}?result_lastmodifieddate=gt.${dwkc.state.oldestDate}`
        );

    dwkc.setState({
        dwkc_log: logData,
        dwkc_emotionCharacterCounts: eccData,
    });

    ReactTooltip.rebuild();
}

export const dwkc_oldestDate = `2020-06-01`;
